import { Injectable } from '@angular/core'
import { Observable, repeat, retry, share, Subject, timer } from 'rxjs'
import { webSocket, WebSocketSubject } from 'rxjs/webSocket'

@Injectable({
	providedIn: 'root',
})
export class WebsocketService<Request, Response> {
	protected readonly map: MapConstructor = Map
	protected readonly set: SetConstructor = Set

	private _sockets: Map<string, WebSocketSubject<Request>> = new this.map<string, WebSocketSubject<Request>>()
	private _responses: Map<string, Observable<Response>> = new this.map<string, Observable<Response>>()
	private _messages: Map<string, Request> = new this.map<string, Request>()
	private _socketsWithFirstMessage: Set<string> = new this.set<string>()

	private _dropCacheSocket(url: string): void {
		this._socketsWithFirstMessage.delete(url)
		this._sockets.delete(url)
		this._responses.delete(url)
		this._messages.delete(url)
	}

	private _createSocket$(url: string): WebSocketSubject<Request> {
		return webSocket<Request>({
			url,
			openObserver: {
				next: (): void => {
					if (this._socketsWithFirstMessage.has(url)) return
					this._socketsWithFirstMessage.add(url)

					const msg: Request | undefined = this._messages.get(url)
					const socket$: WebSocketSubject<Request> | undefined = this._sockets.get(url)

					if (msg && socket$) socket$.next(msg)
				},
			},
			closeObserver: {
				next: (): void => {
					this._dropCacheSocket(url)
				},
			},
		})
	}

	private _createResponse$(socket$: WebSocketSubject<Request>): Observable<Response> {
		return socket$.pipe(
			retry({ count: 15, delay: 5000, resetOnSuccess: true }),
			repeat({ count: 15, delay: 5000 }),
			share({
				connector: (): Subject<Request> => new Subject<Request>(),
				resetOnRefCountZero: (): Observable<0> => timer(5000),
			}),
		) as unknown as Observable<Response>
	}

	public hasMessageInSocket(url: string): boolean {
		return this._messages.has(url)
	}

	public connect(url: string): void {
		if (this._sockets.has(url)) return

		const socket$: WebSocketSubject<Request> = this._createSocket$(url)
		this._sockets.set(url, socket$)

		const response$: Observable<Response> = this._createResponse$(socket$)
		this._responses.set(url, response$)
	}

	public getResponses(url: string): Observable<Response> {
		const response$: Observable<Response> | undefined = this._responses.get(url)
		if (!response$) throw new Error(`WebSocket for ${url} is not connected.`)

		return response$
	}

	public sendMessage(url: string, message: Request): void {
		const socket$: WebSocketSubject<Request> | undefined = this._sockets.get(url)
		if (!socket$) return

		this._socketsWithFirstMessage.add(url)
		this._messages.set(url, message)
		socket$.next(message)
	}

	public disconnect(url: string): void {
		const socket$: WebSocketSubject<Request> | undefined = this._sockets.get(url)
		if (socket$) socket$.unsubscribe()
		this._dropCacheSocket(url)
	}

	public disconnectAll(): void {
		for (const [url] of this._sockets.entries()) {
			this.disconnect(url)
		}
	}
}
